import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ListFilter } from 'lucide-react';
import { Card } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  ComentariosSection,
  DetailDrawer,
  KanbanBoard,
  useKanban,
  type DetailTabSpec,
} from '@/modules/kanban';
import { CREDITO_PIPES } from '@/modules/credito/data';
import {
  creditoCardFieldMap,
  creditoDetailFieldMap,
} from '@/modules/credito/fieldMap';
import { ChecklistDocumentos } from '@/modules/credito/components/ChecklistDocumentos';
import { HistoricoSection } from '@/modules/credito/components/HistoricoSection';
import { BBResumo } from '@/modules/credito/components/BBResumo';
import { SicoobCredisulGuide } from '@/modules/credito/components/SicoobCredisulGuide';
import {
  BancoAmazoniaLogo,
  BancoDoBrasilLogo,
  BradescoLogo,
  CredisisLogo,
  SicrediLogo,
  SicoobLogo,
} from '@/modules/credito/components/InstituicaoLogo';

const BASE_CREDITO_TABS: DetailTabSpec[] = [
  {
    id: 'cadastro',
    label: 'Cadastro',
  },
  {
    id: 'documentos',
    label: 'Documentos',
    slot: (cardId) => <ChecklistDocumentos cardId={cardId} />,
  },
  {
    id: 'historico',
    label: 'Histórico',
    slot: (cardId) => <HistoricoSection cardId={cardId} />,
  },
  {
    id: 'comentarios',
    label: 'Comentários',
    slot: (cardId) => <ComentariosSection dominio="credito" cardId={cardId} />,
  },
];

const ACTIVE_PIPES = CREDITO_PIPES.filter(
  (pipe) => pipe.kind === 'parent' || pipe.kind === 'child' || pipe.kind === 'support',
);

function renderPipeIcon(pipe: (typeof CREDITO_PIPES)[number]) {
  const PipeIcon = pipe.icon;
  if (pipe.brand === 'basa') return <BancoAmazoniaLogo size={27} />;
  if (pipe.brand === 'bb') return <BancoDoBrasilLogo size={27} />;
  if (pipe.brand === 'sicoob') return <SicoobLogo size={27} />;
  if (pipe.brand === 'sicredi') return <SicrediLogo size={27} />;
  if (pipe.brand === 'bradesco') return <BradescoLogo size={27} />;
  if (pipe.brand === 'credisis') return <CredisisLogo size={27} />;
  if (pipe.kind === 'parent') {
    return (
      <span className="flex h-[27px] w-[27px] items-center justify-center rounded-[7px] bg-[var(--accent)]">
        <ListFilter size={13} strokeWidth={1.6} className="text-[var(--on-solid)]" />
      </span>
    );
  }
  return (
    <span className="flex h-[27px] w-[27px] items-center justify-center rounded-[7px] border border-border bg-surface">
      <PipeIcon size={13} strokeWidth={1.6} className="text-text-muted" />
    </span>
  );
}

function PipeSelectorCard({
  pipes,
  selected,
  onSelect,
}: {
  pipes: (typeof CREDITO_PIPES);
  selected: string;
  onSelect: (pipeId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3.5">
      {pipes.map((pipe) => {
        const isSelected = selected === pipe.id;
        const isParent = pipe.kind === 'parent';
        return (
          <button
            key={pipe.id}
            type="button"
            onClick={() => onSelect(pipe.id)}
            className={cn(
              'flex h-[112px] w-[92px] flex-col items-start gap-[7px] rounded-[7px] p-[9px] text-left transition-colors',
              isSelected
                ? 'bg-[var(--pipe-active)]'
                : 'bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:bg-[var(--pipe-active)] hover:shadow-none',
            )}
          >
            {renderPipeIcon(pipe)}
            <span className="text-[13px] font-semibold leading-tight text-text">{pipe.label}</span>
            <span
              className={cn(
                'mt-auto inline-flex items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em]',
                isSelected
                  ? 'bg-[var(--pipe-chip-bg)] text-[var(--pipe-chip-fg)]'
                  : 'bg-surface2 text-text-muted',
              )}
            >
              {isParent ? 'Card pai' : 'Card filho'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PipeSelectorShell({
  pipes,
  selected,
  onSelect,
}: {
  pipes: (typeof CREDITO_PIPES);
  selected: string;
  onSelect: (pipeId: string) => void;
}) {
  return (
    <Card padding="md" className="border-0 bg-surface2 shadow-none">
      <div className="flex flex-col gap-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-text-muted">Selecionar esteira</p>
        <PipeSelectorCard pipes={pipes} selected={selected} onSelect={onSelect} />
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/_authed/credito')({
  validateSearch: (search: Record<string, unknown>) => ({
    card: typeof search.card === 'string' && /^\d+$/.test(search.card) ? search.card : undefined,
    pipe: typeof search.pipe === 'string' && CREDITO_PIPES.some((p) => p.id === search.pipe) ? search.pipe : undefined,
  }),
  component: CreditoPage,
});

function CreditoPage() {
  const search = Route.useSearch();
  const [pipe, setPipe] = useState<string>(search.pipe ?? 'triagem_credito');
  const [selectedCard, setSelectedCard] = useState<string | null>(search.card ?? null);
  useEffect(() => {
    if (search.card) {
      setPipe(search.pipe ?? 'triagem_credito');
      setSelectedCard(search.card);
    }
  }, [search.card, search.pipe]);
  const { data, isLoading, error } = useKanban('credito', pipe);

  const tabs = pipe === 'bb'
    ? [{ id: 'bb', label: 'Banco do Brasil', slot: (cardId: string) => <BBResumo cardId={cardId} /> }, ...BASE_CREDITO_TABS]
    : pipe === 'fno'
      ? [{ id: 'basa', label: 'Banco da Amazônia', slot: (cardId: string) => <BBResumo cardId={cardId} banco="BASA" /> }, ...BASE_CREDITO_TABS]
      : BASE_CREDITO_TABS;

  return (
    <PageShell
      title="Crédito Rural"
      titleSize="sm"
      headerClassName="!pt-4"
    >
      <Stack className="gap-5">
        <PipeSelectorShell
          pipes={ACTIVE_PIPES}
          selected={pipe}
          onSelect={(id) => {
            setSelectedCard(null);
            setPipe(id);
          }}
        />
        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando kanban…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar kanban.'}
            </p>
          </Card>
        ) : data ? (
          <>
            {pipe === 'sicoob_credisul' && <SicoobCredisulGuide />}
            <KanbanBoard
              data={data}
              fieldMap={creditoCardFieldMap}
              dominio="credito"
              pipe={pipe}
              bareSearch
              onCardSelect={setSelectedCard}
            />
            <DetailDrawer
              dominio="credito"
              pipe={pipe}
              cardId={selectedCard}
              columns={data.colunas}
              cardFieldMap={creditoCardFieldMap}
              detailFieldMap={creditoDetailFieldMap}
              tabs={tabs}
              onClose={() => setSelectedCard(null)}
            />
          </>
        ) : null}
      </Stack>
    </PageShell>
  );
}
